import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import { SwimmerLoader } from './components/SwimmerLoader';
import HomePage from './pages/HomePage';
import BookNowPage from './pages/BookNowPage';
import MembershipPage from './pages/MembershipPage';
import TrainingPage from './pages/TrainingPage';
import ContactPage from './pages/ContactPage';
import MyBookingsPage from './pages/MyBookingsPage';
import MyMembershipPage from './pages/MyMembershipPage';
import AdminLayout from './pages/admin/AdminLayout';
import OverviewPage from './pages/admin/OverviewPage';
import MembersPage from './pages/admin/MembersPage';
import MemberDetailPage from './pages/admin/MemberDetailPage';
import MembershipsPage from './pages/admin/MembershipsPage';
import AttendancePage from './pages/admin/AttendancePage';
import BatchesPage from './pages/admin/BatchesPage';
import UsersPage from './pages/admin/UsersPage';
import PaymentsPage from './pages/admin/PaymentsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import AccessoriesPage from './pages/admin/AccessoriesPage';
import IncentivesPage from './pages/admin/IncentivesPage';
import CoachPayoutPage from './pages/admin/CoachPayoutPage';
import LoginModal from './components/LoginModal';
import { Toaster } from './components/ui/toaster';
import { useToast } from './hooks/use-toast';
import { authApi, setSession, clearSession, isAdminRole, TOKEN_KEY, USER_KEY } from './lib/api';
import './App.css';

function AppContent() {
  const [user, setUser] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleLoaderDone = useCallback(() => setLoading(false), []);
  const handleVideoReady = useCallback(() => setVideoReady(true), []);

  // Skip loader on non-homepage routes
  useEffect(() => {
    if (!isHomePage) setLoading(false);
  }, [isHomePage]);

  const handleLogin = (userData, token) => {
    setSession(token, userData);
    setUser(userData);
  };

  const handleLogout = async () => {
    try { await authApi.logout(); } catch (e) { /* ignore */ }
    clearSession();
    setUser(null);
    toast({ title: 'Logged out successfully', description: 'Come back soon!' });
  };

  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);

  // Validate existing session on app load
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch (e) { clearSession(); }
    }
    if (token) {
      authApi.me()
        .then((res) => { setUser(res.data); setSession(null, res.data); })
        .catch(() => { clearSession(); setUser(null); });
    }
  }, []);

  const admin = isAdminRole(user?.role);

  return (
    <>
      {loading && isHomePage && <SwimmerLoader onReady={handleLoaderDone} videoReady={videoReady} />}
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
        <Header
          user={user}
          onLogout={handleLogout}
          onOpenLogin={openLoginModal}
        />
        <Routes>
          <Route path="/" element={<HomePage onVideoReady={handleVideoReady} />} />
          <Route path="/book-now" element={<BookNowPage user={user} onOpenLogin={openLoginModal} />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/coaching" element={<TrainingPage />} />
          <Route path="/training" element={<TrainingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/my-bookings" element={<MyBookingsPage user={user} />} />
          <Route path="/my-membership" element={<MyMembershipPage user={user} onOpenLogin={openLoginModal} />} />
          <Route path="/admin-dashboard" element={
            admin ? (
              <AdminLayout user={user} />
            ) : (
              <div className="min-h-screen flex items-center justify-center" data-testid="access-denied">
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
                  <p className="text-gray-600 mb-6">You need admin privileges to access this page.</p>
                  <button
                    onClick={openLoginModal}
                    data-testid="login-as-admin-btn"
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-2 rounded-md"
                  >
                    Login as Admin
                  </button>
                </div>
              </div>
            )
          }>
            <Route index element={<OverviewPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="members" element={<MembersPage />} />
            <Route path="members/:id" element={<MemberDetailPage />} />
            <Route path="memberships" element={<MembershipsPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="batches" element={<BatchesPage />} />
            <Route path="accessories" element={<AccessoriesPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="incentives" element={<IncentivesPage />} />
            <Route path="coach-payout" element={<CoachPayoutPage />} />
            <Route path="users" element={<UsersPage user={user} />} />
          </Route>
        </Routes>

        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={closeLoginModal}
          onLogin={handleLogin}
        />

        <Toaster />
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
